import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { DateRangeType } from '@ingenico/react-ui';
import type {
  AdvancedSearchApi,
  AdvancedSearchField,
  DatagridQuickFilter,
  DatagridQuickFilterTypeEnum,
  OptionListRequest,
  OverrideResetFieldType,
} from '@ingenico/react-ui/advanced-search';
import {
  AdvancedSearchControl,
  QuickFilterPanel,
  getSetTranslateAdvancedSearch,
} from '@ingenico/react-ui/advanced-search';
import { ListViewHeader } from '@ingenico/react-ui/datagrid';
import { DateRange } from '@ingenico/react-ui/date-range';
import { SearchBarInput } from '@ingenico/react-ui/search-bar-input';
import { SvgAsset } from '@ingenico/react-ui/svg-asset';
import { Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdvancedSearchData } from '../deployment-target-devices-tab/DeploymentTargetDevicesTab.model';
import styles from './DeploymentTargetDevicesTabListFilters.module.scss';

interface DeploymentTargetDevicesTabDateFilter {
  readonly startDate: number | null;
  readonly userLocale?: string;
  readonly endDate: number | null;
  readonly userDateTimeFormat?: string;
  readonly setDateRange: (type: DateRangeType) => (value: Date | null) => void;
}

interface DeploymentTargetDevicesTabListFiltersProps {
  readonly filterRefApi: RefObject<AdvancedSearchApi>;
  readonly fields: AdvancedSearchField[];
  readonly isLoadingQuickFilters: boolean;
  readonly getLabelQuickFilterLabel: (name: string) => string;
  readonly handleFilterChange: (quickFilters: DatagridQuickFilter[]) => void;
  readonly overrideDefaultFilterTypes: (
    field: string,
    currentType: DatagridQuickFilterTypeEnum,
  ) => DatagridQuickFilterTypeEnum;
  readonly checkAllowReset: (filter?: string) => boolean;
  readonly advancedSearch: AdvancedSearchData;
  readonly dateFilter: DeploymentTargetDevicesTabDateFilter;
  readonly overrideResetFieldType?: OverrideResetFieldType;
  readonly quickFiltersOptionListRequests?: OptionListRequest;
  readonly isQuickFilterLoading?: Record<string, boolean>;
  readonly isOpenedFromLogicalDevice?: boolean;
  readonly onRefresh?: () => void;
  readonly quickFiltersErrors?: Record<string, string | undefined>;
}
export function DeploymentTargetDevicesTabListFilters({
  filterRefApi,
  fields,
  isLoadingQuickFilters,
  getLabelQuickFilterLabel,
  handleFilterChange,
  overrideDefaultFilterTypes,
  checkAllowReset,
  advancedSearch,
  dateFilter,
  overrideResetFieldType,
  quickFiltersOptionListRequests,
  isQuickFilterLoading,
  isOpenedFromLogicalDevice = false,
  onRefresh,
  quickFiltersErrors,
}: DeploymentTargetDevicesTabListFiltersProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  const { quickFilterPanelTexts, advancedControlTexts } = getSetTranslateAdvancedSearch(t);
  return (
    <ListViewHeader
      leftSlot={
        <Box className={styles.leftSlotContainer}>
          <Box className={styles.leftSlot__box}>
            <SearchBarInput
              onSearchChange={advancedSearch.handleSearchQueryChange}
              placeholder={t(`search.placeholder`, { ns: ALL_LNGS_NAMESPACES.COMMON })}
              variant="filled"
            />
            <AdvancedSearchControl
              advancedSearchProposals={[]}
              checkAllowReset={checkAllowReset}
              fields={fields}
              getFilterConditionLabel={(filter) => `
                      ${t(filter.field)}
                      ${t(`common:advancedSearch.conditionValues.${filter.condition}`)}
                      ${String(filter.criteria)}
                    `}
              getOperatorsLabel={(operator) => t(`common:advancedSearch.conditionValues.${operator}`)}
              {...advancedControlTexts}
              getQuickFilterLabel={getLabelQuickFilterLabel}
              isLoading={isLoadingQuickFilters}
              onQuickFilterChange={handleFilterChange}
              overrideDefaultFilterTypes={overrideDefaultFilterTypes}
              overrideResetFieldType={overrideResetFieldType}
              quickFiltersOptionListRequests={quickFiltersOptionListRequests}
              ref={filterRefApi}
              isQuickFilterLoading={isQuickFilterLoading}
              quickFiltersErrors={quickFiltersErrors}
            >
              <QuickFilterPanel {...quickFilterPanelTexts} />
            </AdvancedSearchControl>
          </Box>
          {isOpenedFromLogicalDevice && (
            <DateRange
              customClassName={styles.dateRange}
              dateTimeFormat={dateFilter?.userDateTimeFormat}
              endDate={dateFilter.endDate}
              locale={dateFilter?.userLocale}
              onChange={dateFilter?.setDateRange}
              startDate={dateFilter.startDate}
            />
          )}
          <Tooltip title={t('button.refresh', { ns: ALL_LNGS_NAMESPACES.COMMON })}>
            <button type="button" disabled={isLoadingQuickFilters} onClick={onRefresh} className={styles.actionButtons}>
              <SvgAsset image="refresh" />
            </button>
          </Tooltip>
        </Box>
      }
      leftSlotClassName={styles.leftSlotClassName}
    />
  );
}
